import { shouldReduceMotion } from '@theme/utilities';

/**
 * @typedef {Object} OpacityTextAnimationInstance
 * @property {HTMLElement} element
 * @property {HTMLElement[]} words
 * @property {number} baseOpacity
 * @property {number} startOffset
 * @property {number} finishOffset
 */

/** @type {OpacityTextAnimationInstance[]} */
const instances = [];

let frameRequested = false;
let listenersBound = false;

/** @type {AbortController | null} */
let listenersController = null;

/**
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

/**
 * @param {OpacityTextAnimationInstance} instance
 */
function updateInstance(instance) {
  const rect = instance.element.getBoundingClientRect();
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const rawProgress =
    (viewportHeight - rect.top) / (viewportHeight + rect.height);
  const range = Math.max(0.001, instance.finishOffset - instance.startOffset);
  const progress = clamp((rawProgress - instance.startOffset) / range, 0, 1);

  instance.words.forEach((word, index) => {
    const reveal = clamp(progress * instance.words.length - index, 0, 1);
    const opacity = instance.baseOpacity + (1 - instance.baseOpacity) * reveal;
    word.style.opacity = opacity.toFixed(3);
  });
}

function pruneInstances() {
  for (let index = instances.length - 1; index >= 0; index -= 1) {
    const instance = instances[index];
    if (!instance || !instance.element.isConnected) {
      instances.splice(index, 1);
    }
  }
}

function scheduleUpdate() {
  if (frameRequested || shouldReduceMotion()) {
    return;
  }

  frameRequested = true;
  window.requestAnimationFrame(() => {
    frameRequested = false;
    pruneInstances();
    instances.forEach(updateInstance);
  });
}

/**
 * @param {ParentNode} [root]
 */
function initOpacityTextAnimation(root = document) {
  const elements = root.querySelectorAll(
    '[data-opacity-text-animation]:not([data-opacity-text-animation-ready="true"])'
  );

  elements.forEach((element) => {
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const words = Array.from(
      element.querySelectorAll('.opacity-text-animation__word')
    ).filter((word) => word instanceof HTMLElement);

    if (!words.length) {
      return;
    }

    element.dataset.opacityTextAnimationReady = 'true';

    if (shouldReduceMotion()) {
      element.classList.add('opacity-text-animation--reduced-motion');
      words.forEach((word) => {
        word.style.opacity = '1';
      });
      return;
    }

    instances.push({
      element,
      words,
      baseOpacity:
        Number.parseFloat(element.dataset.baseOpacity || '0.15') || 0.15,
      startOffset:
        Number.parseFloat(element.dataset.startOffset || '0.2') || 0.2,
      finishOffset:
        Number.parseFloat(element.dataset.finishOffset || '0.65') || 0.65,
    });
  });

  scheduleUpdate();
}

function bindListeners() {
  if (listenersBound) {
    return;
  }

  listenersBound = true;
  listenersController = new AbortController();
  const { signal } = listenersController;

  window.addEventListener('scroll', scheduleUpdate, { passive: true, signal });
  window.addEventListener('resize', scheduleUpdate, { signal });
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const target = event.target;
      if (target instanceof HTMLElement) {
        initOpacityTextAnimation(target);
      }
    },
    { signal }
  );
  document.addEventListener('shopify:section:reorder', scheduleUpdate, {
    signal,
  });
}

bindListeners();
initOpacityTextAnimation(document);
